import { z } from 'zod'

import { parseDecimalInput, type DecimalInputProblem } from '@/lib/decimalInput'

/**
 * The view state the cashflow page reads from the URL.
 *
 * With no dates the backend chooses twelve weeks from this Monday, so the URL
 * stays empty until someone narrows the window. `open` names the row whose
 * invoices are shown: `<week>~<currency>`, with `overdue` for the overdue row.
 */
const isoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .optional()
  .catch(undefined)

/** An opening amount as the URL keeps it: digits, an optional minus and up to four decimals. */
export const OPENING = /^-?\d+(\.\d{1,4})?$/

const id = z.number().int().positive().optional().catch(undefined)

export const cashflowSearchSchema = z.object({
  warehouse: id.describe('The warehouse id the invoices are narrowed to.'),
  retailer: id.describe('The retailer id the expected receipts are narrowed to.'),
  vendor: id.describe('The vendor id the expected payments are narrowed to.'),
  provider: id.describe('The service provider id the expected payments are narrowed to.'),
  from: isoDate.describe('The first due day of the window, YYYY-MM-DD, inclusive.'),
  to: isoDate.describe(
    'The end of the due window, YYYY-MM-DD; the backend reads it as the day after the last.',
  ),
  // The router parses `?opening=500` as a number, so a number is taken as typed.
  opening: z
    .preprocess((v) => (typeof v === 'number' ? String(v) : v), z.string().regex(OPENING))
    .optional()
    .catch(undefined)
    .describe('The opening cash balance the running balance starts from.'),
  openingCurrency: z
    .string()
    .regex(/^[A-Z]{3}$/)
    .optional()
    .catch(undefined)
    .describe('The ISO currency code of the opening balance.'),
  open: z
    .string()
    .regex(/^(overdue|\d{4}-\d{2}-\d{2})~[A-Z]{3}$/)
    .optional()
    .catch(undefined)
    .describe('The row whose invoices are open: <week start or overdue>~<currency>.'),
})

export type CashflowSearch = z.infer<typeof cashflowSearchSchema>

/** Drop anything empty, so a URL carries only what was chosen. */
export function withoutSearchDefaults(search: CashflowSearch): CashflowSearch {
  const pruned: CashflowSearch = {}
  for (const key of ['warehouse', 'retailer', 'vendor', 'provider'] as const) {
    if (search[key] !== undefined) pruned[key] = search[key]
  }
  if (search.from) pruned.from = search.from
  if (search.to) pruned.to = search.to
  if (search.opening) pruned.opening = search.opening
  if (search.openingCurrency) pruned.openingCurrency = search.openingCurrency
  if (search.open) pruned.open = search.open
  return pruned
}

/** The key `open` uses for a row. */
export function rowKey(weekStart: string | null, currency: string): string {
  return `${weekStart ?? 'overdue'}~${currency}`
}

/** The row an `open` value names. */
export function parseRowKey(key: string): { weekStart: string | null; currency: string } {
  const [week = '', currency = ''] = key.split('~')
  return { weekStart: week === 'overdue' ? null : week, currency }
}

/**
 * An opening balance as typed, as the URL keeps it — or why it cannot be read.
 *
 * The amount is read in the reader's locale (`12,50` is twelve and a half
 * where the comma is the decimal mark, and refused as ambiguous where it is
 * not), and may be negative: an overdraft. Empty clears it. With no currency
 * chosen it takes the only currency on screen, if there is just one; the
 * backend refuses an amount with no currency, so with several it waits for one.
 */
export function openingFrom(
  text: string,
  locale: string,
  currencies: string[],
  chosen: string | undefined,
):
  | { ok: true; opening: string | undefined; openingCurrency: string | undefined }
  | { ok: false; problem: DecimalInputProblem } {
  if (!text.trim()) return { ok: true, opening: undefined, openingCurrency: undefined }
  const read = parseDecimalInput(text, locale, { allowNegative: true })
  if (!read.ok) return { ok: false, problem: read.problem }
  const only = currencies.length === 1 ? currencies[0] : undefined
  return { ok: true, opening: read.value, openingCurrency: chosen ?? only }
}
